import { useEffect, useRef, useState } from "react"
import type { RelistRunMarketplaceInboxStatus } from "@nova-ai/shared"
import { useRelistRunInboxStatus } from "../services/use-runs"
import { useSetRelistPolicy } from "../services/use-relist-policies"

type Change = { policy: RelistRunMarketplaceInboxStatus; enabled: boolean; isUndo: boolean }
type Notice = {
  policy: RelistRunMarketplaceInboxStatus
  previousEnabled: boolean
  canUndo: boolean
}

function needsRelistConfirmation(policy: RelistRunMarketplaceInboxStatus, enabled: boolean) {
  return enabled && (policy.activationEventId !== null || policy.pending + policy.claimed > 0)
}

function focusRelistSwitch(marketplace: string) {
  const control = document.getElementById(`relist-${marketplace}`)
  if (control?.hasAttribute("disabled")) {
    document.getElementById("relist-refresh")?.focus()
  } else {
    control?.focus()
  }
}

export function useRelistSettings() {
  const inbox = useRelistRunInboxStatus()
  const save = useSetRelistPolicy()
  const [confirmation, setConfirmation] = useState<Change | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const [preSavePreference, setPreSavePreference] = useState<{
    marketplace: string
    enabled: boolean
  } | null>(null)
  const focusAfterSave = useRef<string | null>(null)
  const writing = useRef(false)

  useEffect(() => {
    if (!save.isPending && focusAfterSave.current) {
      focusRelistSwitch(focusAfterSave.current)
      focusAfterSave.current = null
    }
  }, [save.isPending])
  useEffect(() => {
    if (!notice) {
      return
    }
    const timer = window.setTimeout(() => {
      if (document.activeElement?.closest("[data-relist-toast]")) {
        focusRelistSwitch(notice.policy.marketplace)
      }
      setNotice(null)
    }, 5000)
    return () => window.clearTimeout(timer)
  }, [notice])

  async function commit(change: Change) {
    if (writing.current || needsRefresh || inbox.isError) {
      return
    }
    const current = inbox.data?.marketplaces.find(
      (row) => row.marketplace === change.policy.marketplace
    )
    if (!current) {
      return
    }
    // Undo's revision token may predate the preference currently displayed.
    setPreSavePreference({ marketplace: current.marketplace, enabled: current.enabled })
    writing.current = true
    setConfirmation(null)
    setNotice(null)
    setErrors((previous) => ({ ...previous, [change.policy.marketplace]: "" }))
    focusAfterSave.current = change.policy.marketplace
    try {
      const result = await save.mutateAsync(change)
      if (!result.ok) {
        setErrors((previous) => ({ ...previous, [change.policy.marketplace]: result.message }))
        setNeedsRefresh(result.requiresRefresh)
        return
      }
      setNotice({
        policy: result.policy,
        previousEnabled: change.policy.enabled,
        canUndo: !change.isUndo,
      })
    } finally {
      writing.current = false
    }
  }

  function request(change: Change) {
    if (writing.current || needsRefresh || inbox.isError) {
      return
    }
    if (needsRelistConfirmation(change.policy, change.enabled)) {
      setConfirmation(change)
    } else {
      void commit(change)
    }
  }

  async function refresh() {
    if (writing.current) {
      return
    }
    const result = await inbox.refetch()
    if (result.isSuccess) {
      setNeedsRefresh(false)
      setErrors({})
    }
  }

  function undo() {
    if (!notice?.canUndo) {
      return
    }
    const current = inbox.data?.marketplaces.find(
      (row) => row.marketplace === notice.policy.marketplace
    )
    // Preserve the saved revision even after background reads reveal newer state.
    // Fresh queue information still decides whether resuming needs confirmation.
    request({
      policy: {
        ...current,
        ...notice.policy,
        pending: current?.pending ?? notice.policy.pending,
        claimed: current?.claimed ?? notice.policy.claimed,
      },
      enabled: notice.previousEnabled,
      isUndo: true,
    })
    setNotice(null)
  }

  return {
    inbox,
    save,
    notice,
    confirmation,
    errors,
    needsRefresh,
    unconfirmedPreference: save.isPending || needsRefresh ? preSavePreference : null,
    refresh,
    undo,
    change: (policy: RelistRunMarketplaceInboxStatus, enabled: boolean) =>
      request({ policy, enabled, isUndo: false }),
    confirm: () => {
      if (confirmation) {
        void commit(confirmation)
      }
    },
    cancel: () => {
      if (confirmation) {
        focusRelistSwitch(confirmation.policy.marketplace)
      }
      setConfirmation(null)
    },
    dismiss: (marketplace: string) => {
      setErrors((previous) => ({ ...previous, [marketplace]: "" }))
      focusRelistSwitch(marketplace)
    },
    dismissNotice: () => {
      if (notice) {
        focusRelistSwitch(notice.policy.marketplace)
      }
      setNotice(null)
    },
  }
}
